import React, { useEffect, useState } from "react";
import {
  ChevronDown,
  ShoppingCart,
  MapPin,
  Gift,
  User,
  MessageCircle,
} from "lucide-react";

const Header = () => {
  const [showHelpPopup, setShowHelpPopup] = useState(false);

  // Show popup when scrolling
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 150) setShowHelpPopup(true);
      else setShowHelpPopup(false);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <header className="w-full bg-black text-white shadow-md">
        {/* 🔸 Promo Bar */}
        <div className="bg-gradient-to-r from-yellow-600 via-yellow-500 to-yellow-600 text-center text-sm py-2">
          <span className="font-semibold">
            ✨ 15% OFF all Wine & Spirits this weekend only! ✨
          </span>
          <a
            href="#"
            className="underline ml-2 hover:text-black font-medium"
          >
            Shop Now
          </a>
        </div>

        {/* 🔸 Main Header */}
        <div className="flex items-center justify-between px-8 py-4 max-w-7xl mx-auto">
          {/* Logo */}
          <div className="text-3xl font-extrabold text-yellow-500">
            Liquor<span className="text-white">Luxe</span>
          </div>

          {/* Search Bar */}
          <div className="flex items-center bg-gray-900 border border-gray-700 rounded-full w-[450px] px-4 py-2">
            <input
              type="text"
              placeholder="Search for wine, spirits, or deals..."
              className="bg-transparent outline-none text-sm text-gray-300 w-full placeholder-gray-400"
            />
            <button className="bg-yellow-500 text-black px-4 py-1 rounded-full text-sm font-semibold hover:bg-yellow-400 transition">
              Search
            </button>
          </div>

          {/* Location + User + Cart */}
          <div className="flex items-center gap-6 text-sm">
            <div className="flex items-center gap-1 hover:text-yellow-400 cursor-pointer">
              <MapPin size={18} /> Choose Location
            </div>

            <div className="hover:text-yellow-400 cursor-pointer flex items-center gap-1">
              <User size={18} /> Sign In
            </div>

            <button className="bg-yellow-500 hover:bg-yellow-400 text-black px-3 py-1 rounded-full flex items-center gap-1 font-semibold">
              <ShoppingCart size={18} /> Cart
            </button>
          </div>
        </div>

        {/* 🔸 Navigation Bar */}
        <nav className="bg-gray-950 border-t border-gray-800">
          <ul className="flex justify-center items-center gap-10 py-3 text-sm font-medium">
            {/* Wine Dropdown */}
            <li className="group relative">
              <button className="flex items-center gap-1 hover:text-yellow-400 transition">
                Wine <ChevronDown size={16} />
              </button>
              <div className="absolute top-full left-0 mt-2 hidden group-hover:block bg-black border border-gray-700 rounded-lg w-48 shadow-xl z-50">
                <a href="#" className="block px-4 py-2 hover:bg-yellow-500/10">
                  Red Wine
                </a>
                <a href="#" className="block px-4 py-2 hover:bg-yellow-500/10">
                  White Wine
                </a>
                <a href="#" className="block px-4 py-2 hover:bg-yellow-500/10">
                  Champagne
                </a>
              </div>
            </li>

            {/* Spirits Dropdown */}
            <li className="group relative">
              <button className="flex items-center gap-1 hover:text-yellow-400 transition">
                Spirits <ChevronDown size={16} />
              </button>
              <div className="absolute top-full left-0 mt-2 hidden group-hover:block bg-black border border-gray-700 rounded-lg w-48 shadow-xl z-50">
                <a href="#" className="block px-4 py-2 hover:bg-yellow-500/10">
                  Whiskey
                </a>
                <a href="#" className="block px-4 py-2 hover:bg-yellow-500/10">
                  Vodka
                </a>
                <a href="#" className="block px-4 py-2 hover:bg-yellow-500/10">
                  Rum
                </a>
                <a href="#" className="block px-4 py-2 hover:bg-yellow-500/10">
                  Gin
                </a>
              </div>
            </li>

            {/* Beer Dropdown */}
            <li className="group relative">
              <button className="flex items-center gap-1 hover:text-yellow-400 transition">
                Beer & Seltzers <ChevronDown size={16} />
              </button>
              <div className="absolute top-full left-0 mt-2 hidden group-hover:block bg-black border border-gray-700 rounded-lg w-48 shadow-xl z-50">
                <a href="#" className="block px-4 py-2 hover:bg-yellow-500/10">
                  Craft Beer
                </a>
                <a href="#" className="block px-4 py-2 hover:bg-yellow-500/10">
                  Imported
                </a>
                <a href="#" className="block px-4 py-2 hover:bg-yellow-500/10">
                  Seasonal
                </a>
              </div>
            </li>

            {/* Hosting Dropdown */}
            <li className="group relative">
              <button className="flex items-center gap-1 hover:text-yellow-400 transition">
                Hosting <ChevronDown size={16} />
              </button>
              <div className="absolute top-full left-0 mt-2 hidden group-hover:block bg-black border border-gray-700 rounded-lg w-48 shadow-xl z-50">
                <a href="#" className="block px-4 py-2 hover:bg-yellow-500/10">
                  Party Essentials
                </a>
                <a href="#" className="block px-4 py-2 hover:bg-yellow-500/10">
                  Cocktail Kits
                </a>
              </div>
            </li>

            {/* Gifts Dropdown */}
            <li className="group relative">
              <button className="flex items-center gap-1 hover:text-yellow-400 transition">
                <Gift size={16} /> Gifts
              </button>
              <div className="absolute top-full left-0 mt-2 hidden group-hover:block bg-black border border-gray-700 rounded-lg w-48 shadow-xl z-50">
                <a href="#" className="block px-4 py-2 hover:bg-yellow-500/10">
                  Gift Sets
                </a>
                <a href="#" className="block px-4 py-2 hover:bg-yellow-500/10">
                  Corporate Gifts
                </a>
                <a href="#" className="block px-4 py-2 hover:bg-yellow-500/10">
                  Personalized
                </a>
              </div>
            </li>

            <li>
              <a
                href="#"
                className="text-yellow-500 font-semibold hover:text-yellow-400"
              >
                My Deals
              </a>
            </li>
          </ul>
        </nav>
      </header>

      {/* 🔹 Floating Chat Button (bottom-right) */}
      <div className="fixed bottom-5 right-5 z-50">
        <button className="bg-yellow-500 text-black p-4 rounded-full shadow-xl hover:bg-yellow-400 transition">
          <MessageCircle size={30} />
        </button>
      </div>

      {/* 🔹 Scroll Popup */}
      {showHelpPopup && (
        <div className="fixed bottom-24 right-6 bg-white border shadow-xl px-4 py-2 rounded-xl text-gray-900 text-sm z-50 animate-popup">
          👋 Hi! Need any help?
        </div>
      )}

      <style>
        {`
          @keyframes popup {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-popup {
            animation: popup 0.3s ease-out;
          }
        `}
      </style>
    </>
  );
};

export default Header;
